// The one way the app talks to /api: the signed-in user's ID token as
// a bearer, the {data, warnings} / {error} envelopes of the door
// (functions/src/http/routes.ts), and one forced token refresh on a
// 401 before giving up — an ID token lasts an hour and the desk keeps
// the page open all day.

import {auth} from "../firebase";
import type {
  AddClientInput, AddClientNoteInput, AddClientNoteWire, AddClientWire, AddSessionInput,
  AddSessionWire, ApiError, ApiErrorCode, ApiFail, ApiOk,
  CancelSessionInput, CancelSessionWire, ChangePaymentChannelInput,
  ChangePaymentChannelWire, ClientBasics, DayBoard, DeskClients, Me,
  UndoAddSessionInput, UndoAddSessionWire,
  MoveSessionInput, MoveSessionWire, PayProviderInput, PayProviderWire,
  RecordSessionInput, RecordSessionWire, SedaDevirInput, SedaDevirWire,
  ConfirmSessionByPhoneInput, ConfirmSessionByPhoneWire,
  LoreConnectStart, LoreDisconnected, LoreStatus, LoreTaskChange, LoreTodo,
  SendSessionConfirmationInput, SendSessionConfirmationWire, SessionDetail,
  UncancelSessionInput, UncancelSessionWire, UndoPayoutInput, UndoPayoutWire,
  UndoSessionRecordInput, UndoSessionRecordWire, UpdateClientBasicsInput,
  UpdateClientBasicsWire,
} from "./contract";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly detail?: unknown;
  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = error.code;
    this.detail = error.detail;
  }
}

export interface CallOptions {
  method?: "GET" | "POST";
  body?: unknown;
  signal?: AbortSignal;
}

async function token(forceRefresh = false): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new ApiRequestError(401, {
    code: "unauthenticated", message: "Giriş yapılmadı.",
  });
  return user.getIdToken(forceRefresh);
}

async function send(path: string, opts: CallOptions, bearer: string): Promise<Response> {
  return fetch(path, {
    method: opts.method ?? "GET",
    headers: {
      authorization: `Bearer ${bearer}`,
      ...(opts.body !== undefined ? {"content-type": "application/json"} : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
}

export async function call<T>(path: string, opts: CallOptions = {}): Promise<ApiOk<T>> {
  let res = await send(path, opts, await token());
  if (res.status === 401) {
    // A stale token, most likely — refresh once and retry.
    res = await send(path, opts, await token(true));
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const err = (body as ApiFail | null)?.error ?? {
      code: "internal" as const,
      message: `Sunucu ${res.status} döndü.`,
    };
    throw new ApiRequestError(res.status, err);
  }
  return body as ApiOk<T>;
}

// ── Routes ────────────────────────────────────────────────

export const getMe = (signal?: AbortSignal) =>
  call<Me>("/api/me", {signal});

export const getDay = (date: string, fresh = false, signal?: AbortSignal) =>
  call<DayBoard>(`/api/day?date=${encodeURIComponent(date)}${fresh ? "&fresh=1" : ""}`,
    {signal});

export const getClient = (clientId: string, signal?: AbortSignal) =>
  call<ClientBasics>(`/api/client/${encodeURIComponent(clientId)}`, {signal});

// Every card, for Seans ekle's search and Yeni danışan's same-name
// check (admin only).
export const getDeskClients = (signal?: AbortSignal) =>
  call<DeskClients>("/api/clients", {signal});

// A danışman's portrait as a data URL (404 when she has none).
export const getProviderPhoto = (providerId: string) =>
  call<{dataUrl: string}>(`/api/provider/${encodeURIComponent(providerId)}/photo`);

// The drawer: one seans in full. The instance id alone names no
// calendar, so the column and the board's day ride along.
export const getSession = (
  providerId: string, eventId: string, date: string, signal?: AbortSignal,
) => call<SessionDetail>(`/api/session/${encodeURIComponent(eventId)}` +
  `?provider=${encodeURIComponent(providerId)}&date=${encodeURIComponent(date)}`, {signal});

// The five actions (and the undo). Ids in; the door validates the
// shape, the use-case the rules. Every one is a POST.
const post = <I, R>(name: string) => (body: I) =>
  call<R>(`/api/actions/${name}`, {method: "POST", body});

export const act = {
  recordSession: post<RecordSessionInput, RecordSessionWire>("record-session"),
  undoSessionRecord:
    post<UndoSessionRecordInput, UndoSessionRecordWire>("undo-session-record"),
  changePaymentChannel:
    post<ChangePaymentChannelInput, ChangePaymentChannelWire>("change-payment-channel"),
  cancelSession: post<CancelSessionInput, CancelSessionWire>("cancel-session"),
  uncancelSession: post<UncancelSessionInput, UncancelSessionWire>("uncancel-session"),
  moveSession: post<MoveSessionInput, MoveSessionWire>("move-session"),
  addClientNote: post<AddClientNoteInput, AddClientNoteWire>("add-client-note"),
  updateClientBasics:
    post<UpdateClientBasicsInput, UpdateClientBasicsWire>("update-client-basics"),
  sendSessionConfirmation:
    post<SendSessionConfirmationInput, SendSessionConfirmationWire>(
      "send-session-confirmation"),
  confirmSessionByPhone:
    post<ConfirmSessionByPhoneInput, ConfirmSessionByPhoneWire>("confirm-session-by-phone"),
  // The Kasa drawer's Ver: a danışman's hakediş, Seda's devir, and the
  // toast's Geri al on either.
  payProvider: post<PayProviderInput, PayProviderWire>("pay-provider"),
  sedaDevir: post<SedaDevirInput, SedaDevirWire>("seda-devir"),
  undoPayout: post<UndoPayoutInput, UndoPayoutWire>("undo-payout"),
  // Seans ekle / Yeni danışan (Onur, 03.11.2026).
  addClient: post<AddClientInput, AddClientWire>("add-client"),
  addSession: post<AddSessionInput, AddSessionWire>("add-session"),
  undoAddSession: post<UndoAddSessionInput, UndoAddSessionWire>("undo-add-session"),
};

// The Yapılacaklar drawer's Lore sign-in: is this person connected,
// start a connect (the URL goes to a popup), forget the connection.
// The token itself never reaches the browser.
export const getLoreStatus = (signal?: AbortSignal) =>
  call<LoreStatus>("/api/lore/status", {signal});
export const startLoreConnect = () =>
  call<LoreConnectStart>("/api/lore/connect", {method: "POST", body: {}});
export const disconnectLore = () =>
  call<LoreDisconnected>("/api/lore/disconnect", {method: "POST", body: {}});

// Step 2: the drawer's list (the person's own Lore tasks due today or
// earlier) and marking one done, or undoing that.
export const getLoreTodo = (signal?: AbortSignal) =>
  call<LoreTodo>("/api/lore/todo", {signal});
export const completeLoreTask = (taskId: string) =>
  call<LoreTaskChange>(`/api/lore/todo/${encodeURIComponent(taskId)}/done`,
    {method: "POST", body: {}});
export const reopenLoreTask = (taskId: string) =>
  call<LoreTaskChange>(`/api/lore/todo/${encodeURIComponent(taskId)}/reopen`,
    {method: "POST", body: {}});

// Türkiye is permanently UTC+3 (no DST since 2016).
export const istToday = (now = new Date()): string =>
  new Date(now.getTime() + 3 * 3600e3).toISOString().slice(0, 10);

export const shiftDate = (date: string, days: number): string =>
  new Date(Date.parse(`${date}T12:00:00Z`) + days * 864e5).toISOString().slice(0, 10);

export function formatDateTr(date: string): string {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric", month: "long", weekday: "long",
  }).format(new Date(`${date}T12:00:00+03:00`));
}
